import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Plus, Edit2, Trash2, Search, Filter, AlertCircle, Calendar } from 'lucide-react';
import './ItemsTab.css';

const ItemsTab = ({ hotelId, token }) => {
  const effectiveToken = token || localStorage.getItem('token') || localStorage.getItem('authToken') || sessionStorage.getItem('token');
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [formData, setFormData] = useState({
    item_name: '',
    category_id: '',
    unit: 'kg',
    reorder_level: '',
    reorder_quantity: '',
    unit_price: '',
    barcode: '',
    description: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const units = ['kg', 'g', 'liters', 'ml', 'pcs', 'boxes', 'bottles', 'cans', 'rolls', 'packets', 'dozen'];

  useEffect(() => {
    if (hotelId && effectiveToken) {
      fetchItems();
      fetchCategories();
    }
  }, [hotelId, effectiveToken, filterCategory, searchTerm]);

  const extractCategoriesFromItems = (itemsList) => {
    if (!Array.isArray(itemsList)) return [];

    const seen = new Set();
    return itemsList
      .filter((item) => item?.category_id && item?.category_name)
      .filter((item) => {
        if (seen.has(item.category_id)) return false;
        seen.add(item.category_id);
        return true;
      })
      .map((item) => ({
        category_id: item.category_id,
        category_name: item.category_name
      }));
  };

  const fetchItems = async () => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/api/inventory/items?hotel_id=${hotelId}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      if (filterCategory) url += `category=${filterCategory}&`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${effectiveToken}` }
      });
      const itemsData = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setItems(itemsData);
      setCategories((prevCategories) => {
        const hasApiCategories = Array.isArray(prevCategories) && prevCategories.length > 0;
        if (hasApiCategories) return prevCategories;
        return extractCategoriesFromItems(itemsData);
      });
      setLoading(false);
    } catch (err) {
      console.error('Error fetching items:', err);
      setError('Failed to load items');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/inventory/categories?hotel_id=${hotelId}`,
        { headers: { Authorization: `Bearer ${effectiveToken}` } }
      );
      const categoriesData = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      if (categoriesData.length > 0) {
        setCategories(categoriesData);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const payload = {
        ...formData,
        hotel_id: hotelId,
        cost_price: parseFloat(formData.unit_price),
        reorder_level: parseFloat(formData.reorder_level),
        reorder_quantity: parseFloat(formData.reorder_quantity),
        current_quantity: 0
      };

      if (editingItem) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/inventory/items/${editingItem.item_id}?hotel_id=${hotelId}`,
          payload,
          { headers: { Authorization: `Bearer ${effectiveToken}` } }
        );
        setSuccess('Item updated successfully');
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/inventory/items?hotel_id=${hotelId}`,
          payload,
          { headers: { Authorization: `Bearer ${effectiveToken}` } }
        );
        setSuccess('Item created successfully');
      }
      
      fetchItems();
      resetForm();
    } catch (err) {
      console.error('Error saving item:', err);
      setError(err.response?.data?.message || 'Failed to save item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      item_name: item.item_name,
      category_id: item.category_id,
      unit: item.unit,
      reorder_level: item.reorder_level,
      reorder_quantity: item.reorder_quantity,
      unit_price: item.cost_price,
      barcode: item.barcode || '',
      description: item.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/inventory/items/${itemId}?hotel_id=${hotelId}`,
        { headers: { Authorization: `Bearer ${effectiveToken}` } }
      );
      setSuccess('Item deleted successfully');
      fetchItems();
    } catch (err) {
      console.error('Error deleting item:', err);
      setError(err.response?.data?.message || 'Failed to delete item');
    }
  };

  const resetForm = () => {
    setFormData({
      item_name: '',
      category_id: '',
      unit: 'kg',
      reorder_level: '',
      reorder_quantity: '',
      unit_price: '',
      barcode: '',
      description: ''
    });
    setEditingItem(null);
    setShowForm(false);
  };

  const getStockStatus = (item) => {
    const quantityOnHand = parseFloat(item.current_quantity ?? item.quantity_on_hand ?? 0);
    if (quantityOnHand <= 0) return { text: 'Out of Stock', class: 'status-danger' };
    if (quantityOnHand <= parseFloat(item.reorder_level ?? 0)) return { text: 'Low Stock', class: 'status-warning' };
    return { text: 'In Stock', class: 'status-success' };
  };

  if (loading) {
    return (
      <div className="tab-loading">
        <div className="loading-spinner"></div>
        <p>Loading items...</p>
      </div>
    );
  }

  return (
    <div className="items-tab">
      <div className="tab-header">
        <h2><Package size={24} /> Inventory Items</h2>
        <button
          className="btn-primary"
          onClick={() => {
            const nextShowForm = !showForm;
            if (nextShowForm) fetchCategories();
            setShowForm(nextShowForm);
          }}
        >
          <Plus size={20} /> {showForm ? 'Cancel' : 'Add Item'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {showForm && (
        <div className="form-container">
          <h3>{editingItem ? 'Edit Item' : 'New Item'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Item Name *</label>
                <input
                  type="text"
                  value={formData.item_name}
                  onChange={(e) => setFormData({...formData, item_name: e.target.value})}
                  required
                  maxLength={200}
                  placeholder="e.g., Laundry Detergent, Coffee Beans"
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                  required
                >
                  <option value="">Select Category</option>
                  {Array.isArray(categories) && categories.length === 0 && (
                    <option value="" disabled>No categories available</option>
                  )}
                  {(Array.isArray(categories) ? categories : []).map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Unit of Measurement *</label>
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData({...formData, unit: e.target.value})}
                  required
                >
                  {units.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Unit Price *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({...formData, unit_price: e.target.value})}
                  required
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Reorder Level *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.reorder_level}
                  onChange={(e) => setFormData({...formData, reorder_level: e.target.value})}
                  required
                  placeholder="Minimum stock level"
                />
              </div>

              <div className="form-group">
                <label>Reorder Quantity *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.reorder_quantity}
                  onChange={(e) => setFormData({...formData, reorder_quantity: e.target.value})}
                  required
                  placeholder="Amount to reorder"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Barcode</label>
              <input
                type="text"
                value={formData.barcode}
                onChange={(e) => setFormData({...formData, barcode: e.target.value})}
                maxLength={100}
                placeholder="Item barcode (optional)"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                placeholder="Item description (optional)"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingItem ? 'Update' : 'Create'} Item
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="filters-bar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <Filter size={20} />
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="">All Categories</option>
            {(Array.isArray(categories) ? categories : []).map(cat => (
              <option key={cat.category_id} value={cat.category_id}>
                {cat.category_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="items-table-container">
        <table className="items-table">
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Category</th>
              <th>Quantity</th>
              <th>Unit</th>
              <th>Status</th>
              <th>Unit Price</th>
              <th>Reorder Level</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(items) ? items : []).map((item) => {
              const status = getStockStatus(item);
              return (
                <tr key={item.item_id}>
                  <td>
                    <strong>{item.item_name}</strong>
                    {item.barcode && <div className="item-barcode">{item.barcode}</div>}
                  </td>
                  <td>{item.category_name}</td>
                  <td><strong>{item.current_quantity ?? item.quantity_on_hand ?? 0}</strong></td>
                  <td>{item.unit || item.unit_of_measurement || '-'}</td>
                  <td>
                    <span className={`status-badge ${status.class}`}>
                      {status.text}
                    </span>
                  </td>
                  <td>${parseFloat(item.cost_price ?? item.unit_price ?? 0).toFixed(2)}</td>
                  <td>{item.reorder_level}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-icon" 
                        onClick={() => handleEdit(item)}
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        className="btn-icon btn-danger" 
                        onClick={() => handleDelete(item.item_id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {items.length === 0 && (
        <div className="empty-state">
          <Package size={48} />
          <h3>No Items Found</h3>
          <p>Add inventory items to start tracking stock levels</p>
        </div>
      )}
    </div>
  );
};

export default ItemsTab;
